// ATM overlay — move money between carried CASH and the BANK. Deposit = cash → bank,
// withdraw = bank → cash. The server is authoritative; this just sends the requested amount
// and mirrors the live cash/bank totals (balanceStore + statsStore).

import { injectTheme } from "./theme.js";
import { injectBillStyle, cashIcon, bankIcon } from "./pixelBills.js";
import { balanceStore } from "../items/balanceStore.js";
import { statsStore } from "../items/statsStore.js";

export interface AtmActions {
  deposit: (amount: number) => void;
  withdraw: (amount: number) => void;
}
let actions: AtmActions | null = null;
export function setAtmActions(a: AtmActions): void { actions = a; }

const STYLE_ID = "ghostland-atm-style";
const CSS = `
#gl-atm-overlay {
  position: fixed; inset: 0; z-index: 30; display: none;
  align-items: center; justify-content: center; background: rgba(5,0,7,.55);
  font-family: ui-monospace, "SFMono-Regular", Menlo, monospace; color: var(--gl-text);
}
#gl-atm-overlay.open { display: flex; }
#gl-atm-card {
  width: 320px; padding: 22px; background: var(--gl-panel);
  border: 1px solid var(--gl-border); border-radius: 12px; box-shadow: 0 16px 50px rgba(0,0,0,.7);
}
#gl-atm-card h1 { margin: 0 0 4px; font-size: 16px; letter-spacing: 2px; }
#gl-atm-card .hint { font-size: 11px; color: var(--gl-muted); margin-bottom: 16px; }
.gl-atm-row { display: flex; justify-content: space-between; font-size: 13px; margin: 6px 0; }
.gl-atm-row b { color: var(--gl-selected); }
#gl-atm-amt {
  width: 100%; margin: 14px 0 10px; padding: 9px 10px; box-sizing: border-box;
  background: var(--gl-panel-2); border: 1px solid var(--gl-border); border-radius: 7px;
  color: var(--gl-text); font-family: inherit; font-size: 14px;
}
.gl-atm-quick { display: flex; gap: 6px; margin-bottom: 14px; }
.gl-atm-quick button {
  flex: 1; padding: 6px 0; background: var(--gl-panel-2); border: 1px solid var(--gl-border);
  border-radius: 6px; color: var(--gl-muted); font-family: inherit; font-size: 11px; cursor: pointer;
}
.gl-atm-quick button:hover { color: var(--gl-text); border-color: var(--gl-border-focus); }
.gl-atm-btns { display: flex; gap: 10px; }
.gl-atm-btns button {
  flex: 1; padding: 10px 0; border: 1px solid var(--gl-border); border-radius: 7px; cursor: pointer;
  font-family: inherit; font-size: 13px; color: var(--gl-text); background: var(--gl-panel-2);
}
.gl-atm-btns button:hover { border-color: var(--gl-border-focus); }
#gl-atm-dep { background: var(--gl-accent); color: var(--gl-accent-fg); border-color: var(--gl-accent); }
#gl-atm-dep:hover { background: var(--gl-accent-hover); border-color: var(--gl-accent-hover); }
#gl-atm-close { margin-top: 12px; width: 100%; padding: 7px 0; background: none;
  border: 1px solid var(--gl-border); border-radius: 6px; color: var(--gl-muted); cursor: pointer; font-family: inherit; font-size: 11px; }
`;

let overlay: HTMLDivElement | null = null;
let cashEl: HTMLDivElement | null = null;
let bankEl: HTMLDivElement | null = null;
let amtEl: HTMLInputElement | null = null;
let unsub: Array<() => void> = [];

function refresh(): void {
  if (cashEl) cashEl.innerHTML = `${cashIcon()} Cash <b>$${balanceStore.get().toLocaleString()}</b>`;
  if (bankEl) bankEl.innerHTML = `${bankIcon()} Bank <b>$${statsStore.get().bankMoney.toLocaleString()}</b>`;
}

function ensure(): void {
  injectTheme();
  injectBillStyle();
  if (!document.getElementById(STYLE_ID)) {
    const s = document.createElement("style"); s.id = STYLE_ID; s.textContent = CSS; document.head.appendChild(s);
  }
  if (overlay) return;
  overlay = document.createElement("div");
  overlay.id = "gl-atm-overlay";
  overlay.innerHTML = `
    <div id="gl-atm-card">
      <h1>ATM</h1>
      <div class="hint">move money between cash and the bank</div>
      <div class="gl-atm-row" id="gl-atm-cash"></div>
      <div class="gl-atm-row" id="gl-atm-bank"></div>
      <input id="gl-atm-amt" type="number" min="1" step="1" placeholder="amount" />
      <div class="gl-atm-quick">
        <button data-q="100">+100</button><button data-q="1000">+1k</button>
        <button data-q="allcash">All cash</button><button data-q="allbank">All bank</button>
      </div>
      <div class="gl-atm-btns">
        <button id="gl-atm-dep">Deposit</button>
        <button id="gl-atm-wd">Withdraw</button>
      </div>
      <button id="gl-atm-close">Close (Esc)</button>
    </div>`;
  document.body.appendChild(overlay);
  cashEl = overlay.querySelector("#gl-atm-cash");
  bankEl = overlay.querySelector("#gl-atm-bank");
  amtEl = overlay.querySelector("#gl-atm-amt");
  const amt = (): number => Math.max(0, Math.floor(Number(amtEl!.value) || 0));
  overlay.querySelector("#gl-atm-dep")!.addEventListener("click", () => { if (amt() > 0) actions?.deposit(amt()); });
  overlay.querySelector("#gl-atm-wd")!.addEventListener("click", () => { if (amt() > 0) actions?.withdraw(amt()); });
  overlay.querySelector("#gl-atm-close")!.addEventListener("click", closeAtm);
  overlay.addEventListener("click", (e) => { if (e.target === overlay) closeAtm(); });
  overlay.querySelectorAll<HTMLButtonElement>(".gl-atm-quick button").forEach((b) => {
    b.addEventListener("click", () => {
      const q = b.dataset.q!;
      if (q === "allcash") amtEl!.value = String(balanceStore.get());
      else if (q === "allbank") amtEl!.value = String(statsStore.get().bankMoney);
      else amtEl!.value = String(amt() + Number(q));
    });
  });
}

export function openAtm(): void {
  ensure();
  refresh();
  unsub = [balanceStore.subscribe(refresh), statsStore.subscribe(refresh)];
  overlay!.classList.add("open");
}

export function closeAtm(): void {
  overlay?.classList.remove("open");
  unsub.forEach((fn) => fn()); unsub = [];
}

export function isAtmOpen(): boolean {
  return overlay?.classList.contains("open") ?? false;
}
